'use client'
import React, { useState, useEffect } from 'react'
import { Clock } from 'lucide-react'

interface Props {
    endDate: string
    eventName?: string
}

const EventCountdown = ({ endDate, eventName }: Props) => {
    const calcTimeLeft = () => {
        let target: Date

        const isLaborDay = eventName?.toLowerCase().includes('labor') || eventName?.toLowerCase().includes('labour')

        if (isLaborDay) {
            // Labor Day 2026 is September 7, 2026 - count down until end of September 7th
            target = new Date(2026, 8, 7, 23, 59, 59, 999)
        } else if (endDate) {
            const trimmed = endDate.trim()
            if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                const [y, m, d] = trimmed.split('-').map(Number)
                target = new Date(y, m - 1, d, 23, 59, 59, 999)
            } else if (/^\d{4}\/\d{2}\/\d{2}$/.test(trimmed)) {
                const [y, m, d] = trimmed.split('/').map(Number)
                target = new Date(y, m - 1, d, 23, 59, 59, 999)
            } else if (/^\d{2}[/-]\d{2}[/-]\d{4}$/.test(trimmed)) {
                const parts = trimmed.split(/[/-]/).map(Number)
                target = new Date(parts[2], parts[1] - 1, parts[0], 23, 59, 59, 999)
            } else {
                const parsed = new Date(trimmed)
                if (isNaN(parsed.getTime())) {
                    return { days: 0, hours: 0, mins: 0, secs: 0, expired: true }
                }
                if (parsed.getHours() === 0 && parsed.getMinutes() === 0 && parsed.getSeconds() === 0) {
                    parsed.setHours(23, 59, 59, 999)
                }
                target = parsed
            }
        } else {
            return { days: 0, hours: 0, mins: 0, secs: 0, expired: true }
        }

        const diff = target.getTime() - Date.now()
        if (diff <= 0) return { days: 0, hours: 0, mins: 0, secs: 0, expired: true }
        return {
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
            mins: Math.floor((diff / (1000 * 60)) % 60),
            secs: Math.floor((diff / 1000) % 60),
            expired: false,
        }
    }

    const [time, setTime] = useState(calcTimeLeft)
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
        const timer = setInterval(() => setTime(calcTimeLeft()), 1000)
        return () => clearInterval(timer)
    }, [endDate])

    const pad = (n: number) => String(n).padStart(2, '0')

    const units = [
        { value: pad(time.days), label: 'Days' },
        { value: pad(time.hours), label: 'Hours' },
        { value: pad(time.mins), label: 'Mins' },
        { value: pad(time.secs), label: 'Secs' },
    ]

    return (
        <div className="bg-[#071822] rounded-3xl p-7 sm:p-8 text-white shadow-xl border border-emerald-900/40 relative overflow-hidden" suppressHydrationWarning>
            <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 mb-3">
                    <Clock size={20} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold tracking-tight" suppressHydrationWarning>
                    {mounted ? (time.expired ? 'Event Has Concluded' : 'Event Ends In') : 'Event Ends In'}
                </h3>
                {eventName && <p className="text-xs text-slate-400 mt-1 font-medium">{eventName}</p>}
            </div>

            {mounted && time.expired ? (
                <p className="text-center text-slate-400 font-medium text-xs">This limited promotion is now expired.</p>
            ) : (
                <div className="grid grid-cols-4 gap-2 sm:gap-3" suppressHydrationWarning>
                    {units.map((item, i) => (
                        <div key={i} className="text-center">
                            <div className="bg-white/[0.05] border border-white/10 rounded-2xl p-3 sm:p-4 backdrop-blur-xs">
                                <div className="text-2xl sm:text-3xl font-extrabold tabular-nums text-white" suppressHydrationWarning>
                                    {mounted ? item.value : '--'}
                                </div>
                                <div className="text-[10px] uppercase font-bold text-emerald-400 mt-0.5 tracking-wider">{item.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default EventCountdown
